/**
 * Small per-person UI choices, kept in a cookie.
 *
 * A cookie rather than the server, because none of this is data about the
 * team: it is how one person likes the form to look, and a round trip plus
 * a column for it would be more machinery than the choice is worth.
 *
 * Not HttpOnly and not a secret — the browser writes and reads it, and the
 * Worker never looks at it. Everything here has to survive the cookie being
 * absent, blocked or holding a value from a future version of the app, so
 * every read falls back to a default rather than trusting what it finds.
 */

import type { Pinned } from '#shared/recent'
import { orderPins } from '#shared/recent'

/** How the log form asks for a duration. */
export type LogMode = 'hours' | 'range'

const LOG_MODE = 'tt_log_mode'

/** A year: long enough that nobody re-picks it, short enough to lapse. */
const ONE_YEAR = 60 * 60 * 24 * 365

function read(name: string): string | null {
  // document.cookie throws in a sandboxed frame and is empty when cookies
  // are blocked; neither is a reason to fail to render a form.
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

function write(name: string, value: string): void {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${ONE_YEAR}; samesite=lax`
  } catch {
    // A choice that cannot be remembered still applies for this visit.
  }
}

export function readLogMode(): LogMode {
  return read(LOG_MODE) === 'range' ? 'range' : 'hours'
}

export function writeLogMode(mode: LogMode): void {
  write(LOG_MODE, mode)
}

const PINS = 'tt_pins'

/**
 * How many shortcuts one person may keep.
 *
 * A cookie is about 4KB and every request carries it, so this is a real
 * limit rather than a tidiness rule. A dozen pins is far more than the list
 * can usefully show and still leaves the cookie small.
 */
export const MAX_PINS = 12

/** Anything that is not a well-formed pin is discarded rather than trusted. */
function asPin(value: unknown): Pinned | null {
  if (typeof value !== 'object' || value === null) return null
  const row = value as Record<string, unknown>

  if (typeof row.projectId !== 'string' || row.projectId === '') return null
  if (typeof row.minutes !== 'number' || !Number.isFinite(row.minutes)) return null
  if (typeof row.pinnedAt !== 'string') return null
  if (!Array.isArray(row.tagIds) || row.tagIds.some((id) => typeof id !== 'string')) return null
  if (row.notes !== null && typeof row.notes !== 'string') return null

  return {
    projectId: row.projectId,
    tagIds: row.tagIds as string[],
    minutes: row.minutes,
    notes: row.notes as string | null,
    pinnedAt: row.pinnedAt,
  }
}

/**
 * The pinned shortcuts, oldest first.
 *
 * The cookie can hold anything: a value from an older version of the app,
 * something truncated, something a person edited by hand. A shortcut list
 * is not worth an error page, so a cookie that will not parse reads as no
 * pins at all.
 */
export function readPins(): Pinned[] {
  const raw = read(PINS)
  if (raw === null) return []

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return orderPins(parsed.map(asPin).filter((pin): pin is Pinned => pin !== null))
  } catch {
    return []
  }
}

/**
 * Replaces the stored pins, keeping the oldest when there are too many —
 * the list is ordered oldest first, so trimming the newest is what keeps
 * the top of somebody's list where they left it.
 *
 * Returns whether the cookie actually took the value. Writing a cookie
 * fails silently: too large, blocked, third-party context. The caller
 * needs to know, because a Pin button that appears to work and forgets by
 * the next page load is worse than one that says it could not.
 */
export function writePins(pins: readonly Pinned[]): boolean {
  const keep = orderPins(pins).slice(0, MAX_PINS)
  write(PINS, JSON.stringify(keep))

  // Read it back rather than assume: this is the only way to find out.
  return readPins().length === keep.length
}
